import { debApiBase, isApiConfigured } from "./config";
import { ensureAccessToken, isAuthenticated } from "./auth";

const SESSION_KEY = "deb_analytics_sid";
const ANON_KEY = "deb_analytics_aid";
const SESSION_STARTED_KEY = "deb_analytics_session_started";

const FLUSH_MS = 2000;
const MAX_BATCH = 20;

export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

export type AnalyticsEvent = {
  event_id: string;
  event_time: string;
  event_name: string;
  session_id: string;
  anonymous_id: string;
  page?: string;
  city?: string;
  ticketing_event_id?: string;
  properties?: Record<string, string | number | boolean>;
};

const queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;
let pagehideBound = false;
let memorySessionId = "";
let memoryAnonymousId = "";

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch {
      // insecure context
    }
  }
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function storageGet(store: Storage | undefined, key: string, memory: { current: string }): string {
  if (memory.current) return memory.current;
  try {
    const existing = store?.getItem(key);
    if (existing) {
      memory.current = existing;
      return existing;
    }
  } catch {
    // jsdom / insecure contexts
  }
  const id = randomId();
  memory.current = id;
  try {
    store?.setItem(key, id);
  } catch {
    // ignore
  }
  return id;
}

export function analyticsSessionId(): string {
  const store = typeof window !== "undefined" ? window.sessionStorage : undefined;
  return storageGet(store, SESSION_KEY, { get current() { return memorySessionId; }, set current(v) { memorySessionId = v; } });
}

export function analyticsAnonymousId(): string {
  const store = typeof window !== "undefined" ? window.localStorage : undefined;
  return storageGet(store, ANON_KEY, { get current() { return memoryAnonymousId; }, set current(v) { memoryAnonymousId = v; } });
}

function compactProps(props?: AnalyticsProps): Record<string, string | number | boolean> | undefined {
  if (!props) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;
    out[key] = value;
  }
  return Object.keys(out).length ? out : undefined;
}

export function buildEvent(
  name: string,
  props?: AnalyticsProps,
  extras?: { page?: string; city?: string; ticketingEventId?: string },
): AnalyticsEvent {
  return {
    event_id: randomId(),
    event_time: new Date().toISOString(),
    event_name: name,
    session_id: analyticsSessionId(),
    anonymous_id: analyticsAnonymousId(),
    page: extras?.page ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
    city: extras?.city,
    ticketing_event_id: extras?.ticketingEventId,
    properties: compactProps(props),
  };
}

function bindPagehide() {
  if (pagehideBound || typeof window === "undefined") return;
  pagehideBound = true;
  window.addEventListener("pagehide", () => {
    void flush({ keepalive: true });
  });
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_MS);
}

async function postEvents(path: string, events: AnalyticsEvent[], keepalive: boolean, token?: string | null) {
  if (!isApiConfigured() || events.length === 0) return;
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  await fetch(`${debApiBase()}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ events }),
    keepalive,
  });
}

export async function flush(opts?: { keepalive?: boolean }): Promise<void> {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.splice(0, MAX_BATCH);
  try {
    const token = isAuthenticated() ? await ensureAccessToken() : null;
    if (token) {
      await postEvents("/api/v1/protected/community/analytics/events", batch, !!opts?.keepalive, token);
    }
  } catch {
    // Analytics must never break the app.
  } finally {
    flushing = false;
    if (queue.length > 0) scheduleFlush();
  }
}

/** Authenticated product event. Dropped until the user has a session. */
export function track(
  name: string,
  props?: AnalyticsProps,
  extras?: { page?: string; city?: string; ticketingEventId?: string },
): void {
  if (typeof window === "undefined" || !isAuthenticated()) return;
  bindPagehide();
  if (!sessionStorage.getItem(SESSION_STARTED_KEY)) {
    sessionStorage.setItem(SESSION_STARTED_KEY, "1");
    queue.push(buildEvent("session_started", undefined, extras));
  }
  queue.push(buildEvent(name, props, extras));
  if (queue.length >= MAX_BATCH) {
    void flush();
    return;
  }
  scheduleFlush();
}

/** Unauthenticated beacon (login + public help). Never throws. */
export function trackBeacon(
  name: string,
  props?: AnalyticsProps,
  extras?: { page?: string; city?: string; ticketingEventId?: string },
): void {
  if (typeof window === "undefined" || !isApiConfigured()) return;
  const ev = buildEvent(name, props, extras);
  void postEvents("/api/v1/public/analytics/beacon", [ev], true).catch(() => {});
}
